import { useState, type FormEvent } from 'react';
import { MessageCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Pill } from './_primitives';

export interface CommentReply {
  id: string;
  authorName: string;
  authorAvatarUrl?: string | null;
  body: string;
  /** ISO timestamp. */
  createdAt: string;
}

export interface RailComment {
  id: string;
  authorName: string;
  authorAvatarUrl?: string | null;
  body: string;
  createdAt: string;
  resolved?: boolean;
  /** Quoted text from the doc this comment was anchored to. */
  quote?: string | null;
  replies?: CommentReply[];
}

export interface CommentsListProps {
  comments: RailComment[];
  onReply?: (commentId: string, body: string) => void | Promise<void>;
  onResolve?: (commentId: string) => void;
  /** When provided, shown as the avatar/name of the current user replying. */
  currentUserName?: string;
}

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function CommentsList({ comments, onReply, onResolve, currentUserName }: CommentsListProps) {
  const open = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);
  const [showResolved, setShowResolved] = useState(false);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-warm-bg/60 shrink-0">
        <MessageCircle className="size-3.5 text-muted-foreground" />
        <div className="text-[12px] font-bold text-foreground">Comments</div>
        <div className="flex-1" />
        <Pill tone="neutral" size="sm">
          {open.length} open
        </Pill>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
        {open.length === 0 && resolved.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground py-6">
            No comments yet. Highlight text in the doc to start one.
          </div>
        )}

        {open.map((c) => (
          <CommentItem
            key={c.id}
            comment={c}
            onReply={onReply}
            onResolve={onResolve}
            currentUserName={currentUserName}
          />
        ))}

        {resolved.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowResolved((v) => !v)}
              className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer mb-2"
            >
              {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved
            </button>
            {showResolved && (
              <div className="flex flex-col gap-3 opacity-70">
                {resolved.map((c) => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    onReply={onReply}
                    onResolve={onResolve}
                    currentUserName={currentUserName}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface CommentItemProps {
  comment: RailComment;
  onReply?: (commentId: string, body: string) => void | Promise<void>;
  onResolve?: (commentId: string) => void;
  currentUserName?: string;
}

function CommentItem({ comment, onReply, onResolve, currentUserName }: CommentItemProps) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const submitReply = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    onReply?.(comment.id, trimmed);
    setDraft('');
    setReplyOpen(false);
  };

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      {comment.quote && (
        <div className="px-3 py-1.5 bg-coral-light border-l-2 border-coral text-[11px] text-coral-dark italic line-clamp-2">
          “{comment.quote}”
        </div>
      )}
      <div className="p-3">
        <div className="flex items-start gap-2">
          <Avatar name={comment.authorName} url={comment.authorAvatarUrl} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] font-semibold text-foreground truncate">
                {comment.authorName}
              </span>
              <span className="text-[10.5px] text-muted-foreground shrink-0">
                {relativeTime(comment.createdAt)}
              </span>
              {comment.resolved && (
                <Pill tone="success" size="sm" icon={<Check className="size-2.5" />}>
                  Resolved
                </Pill>
              )}
            </div>
            <div className="text-[12.5px] text-foreground leading-relaxed mt-0.5 whitespace-pre-wrap">
              {comment.body}
            </div>
          </div>
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="ml-7 mt-2.5 flex flex-col gap-2 border-l border-border pl-2.5">
            {comment.replies.map((r) => (
              <div key={r.id} className="flex items-start gap-2">
                <Avatar name={r.authorName} url={r.authorAvatarUrl} size={20} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11.5px] font-semibold text-foreground truncate">
                      {r.authorName}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{relativeTime(r.createdAt)}</span>
                  </div>
                  <div className="text-[12px] text-foreground leading-snug whitespace-pre-wrap">
                    {r.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!comment.resolved && (
          <div className="ml-7 mt-2 flex items-center gap-2">
            {!replyOpen ? (
              <>
                <button
                  type="button"
                  onClick={() => setReplyOpen(true)}
                  className="text-[11.5px] text-teal-dark hover:text-teal hover:underline cursor-pointer"
                >
                  Reply
                </button>
                {onResolve && (
                  <button
                    type="button"
                    onClick={() => onResolve(comment.id)}
                    className="text-[11.5px] text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    Resolve
                  </button>
                )}
              </>
            ) : (
              <form onSubmit={submitReply} className="w-full flex items-center gap-1.5">
                <input
                  autoFocus
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={currentUserName ? `Reply as ${currentUserName}…` : 'Reply…'}
                  className={cn(
                    'flex-1 px-2 py-1 text-[12px] border border-border rounded-md bg-card outline-none',
                    'focus:border-coral focus:ring-2 focus:ring-coral/20',
                  )}
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="px-2 py-1 rounded-md text-[11.5px] font-semibold bg-coral text-white border border-coral hover:bg-coral-hover cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Send
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyOpen(false);
                    setDraft('');
                  }}
                  className="px-2 py-1 rounded-md text-[11.5px] text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Avatar({ name, url, size = 24 }: { name: string; url?: string | null; size?: number }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        width={size}
        height={size}
        className="rounded-full shrink-0 object-cover"
      />
    );
  }
  return (
    <div
      className="rounded-full bg-coral-light text-coral-dark grid place-items-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {initials(name) || '?'}
    </div>
  );
}
